import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import fileService from '../services/fileService';

const UploadContext = createContext();

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within UploadProvider');
  }
  return context;
};

export const UploadProvider = ({ children }) => {
  const [uploadJobs, setUploadJobs] = useState([]);
  const [activePolling, setActivePolling] = useState({});

  // Add new upload job
  const addUploadJob = useCallback((job) => {
    const newJob = {
      id: job.importJobUuid,
      importJobUuid: job.importJobUuid,
      dataroomUuid: job.dataroomUuid,
      fileName: job.fileName || 'Bulk Upload',
      fileCount: job.fileCount || 0,
      folderCount: job.folderCount || 0,
      status: 'uploading', // uploading, processing, completed, failed
      progress: 0,
      processed: 0,
      total: job.fileCount || 0,
      failed: 0,
      startTime: new Date().toISOString(),
      error: null
    };

    setUploadJobs(prev => [newJob, ...prev]);
    return newJob.id;
  }, []);

  // Update upload job
  const updateUploadJob = useCallback((jobId, updates) => {
    setUploadJobs(prev =>
      prev.map(job =>
        job.id === jobId ? { ...job, ...updates } : job
      )
    );
  }, []);

  // Remove upload job
  const removeUploadJob = useCallback((jobId) => {
    setUploadJobs(prev => prev.filter(job => job.id !== jobId));

    // Stop polling if active
    if (activePolling[jobId]) {
      clearInterval(activePolling[jobId]);
      setActivePolling(prev => {
        const { [jobId]: removed, ...rest } = prev;
        return rest;
      });
    }
  }, [activePolling]);

  // Clear all completed jobs
  const clearCompleted = useCallback(() => {
    setUploadJobs(prev => prev.filter(job =>
      job.status !== 'completed' && job.status !== 'failed'
    ));
  }, []);

  // Poll progress for a job
  const pollJobProgress = useCallback(async (dataroomUuid, importJobUuid) => {
    try {
      const data = await fileService.getImportProgress(dataroomUuid, importJobUuid);

      console.log('📊 Polling progress for job:', importJobUuid, data);

      if (data) {
        const {
          status,
          progress_percent,
          processed_items,
          total_items,
          files
        } = data;

        const progress = progress_percent || 0;
        const processed = processed_items || 0;
        const total = total_items || 0;
        const failed = files?.failed || 0;

        console.log(`📈 Job ${importJobUuid}: ${progress}% (${processed}/${total}), Status: ${status}`);

        // Map API status to our internal status
        let jobStatus = 'processing';
        if (status === 'completed') {
          jobStatus = 'completed';
        } else if (status === 'failed' || status === 'error') {
          jobStatus = 'failed';
        } else if (status === 'processing' || status === 'pending') {
          jobStatus = 'processing';
        }

        updateUploadJob(importJobUuid, {
          status: jobStatus,
          progress,
          processed,
          total,
          failed
        });

        // Stop polling if completed or failed
        if (status === 'completed' || status === 'failed' || status === 'error') {
          console.log(`✅ Upload job ${importJobUuid} finished with status: ${status}. Stopping polling.`);
          if (activePolling[importJobUuid]) {
            clearInterval(activePolling[importJobUuid]);
            setActivePolling(prev => {
              const { [importJobUuid]: removed, ...rest } = prev;
              return rest;
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to poll progress:', error);
      // Don't update status on single poll error
    }
  }, [updateUploadJob, activePolling]);

  // Start polling for a job
  const startPolling = useCallback((dataroomUuid, importJobUuid) => {
    // Don't start if already polling
    if (activePolling[importJobUuid]) {
      return;
    }

    // Initial poll
    pollJobProgress(dataroomUuid, importJobUuid);

    // Start interval polling every 2 seconds
    const intervalId = setInterval(() => {
      pollJobProgress(dataroomUuid, importJobUuid);
    }, 2000);

    setActivePolling(prev => ({
      ...prev,
      [importJobUuid]: intervalId
    }));
  }, [activePolling, pollJobProgress]);

  // Cleanup all intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(activePolling).forEach(intervalId => {
        clearInterval(intervalId);
      });
    };
  }, [activePolling]);

  // Get counts
  const getCounts = useCallback(() => {
    const uploading = uploadJobs.filter(j => j.status === 'uploading' || j.status === 'processing').length;
    const completed = uploadJobs.filter(j => j.status === 'completed').length;
    const failed = uploadJobs.filter(j => j.status === 'failed').length;

    return { uploading, completed, failed, total: uploadJobs.length };
  }, [uploadJobs]);

  const value = {
    uploadJobs,
    addUploadJob,
    updateUploadJob,
    removeUploadJob,
    clearCompleted,
    startPolling,
    getCounts
  };

  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  );
};
